import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

interface RecoveryCodesPanelProps {
  codes: string[];
  /** Called once the user has explicitly acknowledged saving them. */
  onAcknowledge: () => void;
}

/**
 * The one and only showing of a set of recovery codes.
 *
 * The server stores digests, so these characters cannot be retrieved again —
 * the acknowledgement is deliberately an explicit action rather than a
 * dismissable notice, because clicking past this screen is how people end up
 * locked out of their own money.
 */
export const RecoveryCodesPanel: React.FC<RecoveryCodesPanelProps> = ({
  codes,
  onAcknowledge,
}) => {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const asText = codes.join('\n') + '\n';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the codes are on screen regardless,
      // so this is not worth an error state.
    }
  };

  const download = () => {
    const blob = new Blob(
      [`FolioWorth recovery codes\nEach code works once.\n\n${asText}`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'folioworth-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 650, mb: 1 }}>Save your recovery codes</Typography>
      <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, mb: 2.5, maxWidth: 560 }}>
        Each code signs you in once if you lose your phone. This is the only
        time they are shown — we store only a one-way hash, so we cannot show
        them to you again or recover them on your behalf.
      </Typography>

      <Box
        component="ul"
        aria-label="Recovery codes"
        sx={{
          listStyle: 'none', m: 0, mb: 2, p: 0,
          display: 'grid', gap: 0,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 4, maxWidth: 480,
        }}
      >
        {codes.map((code) => (
          <Box
            key={code}
            component="li"
            className="fw-num"
            sx={{
              py: 0.9, fontSize: '0.9375rem',
              borderTop: '1px solid var(--border-soft)',
            }}
          >
            {code}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
        <Button variant="outlined" size="small" onClick={copy}>
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
        <Button variant="outlined" size="small" onClick={download}>
          Download as a file
        </Button>
      </Box>

      <Box sx={{ borderTop: '1px solid var(--border-soft)', pt: 2 }}>
        <Box
          component="label"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.25,
            cursor: 'pointer', mb: 2,
          }}
        >
          <Box
            component="input"
            type="checkbox"
            checked={saved}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaved(e.target.checked)}
            sx={{ mt: '3px', accentColor: 'var(--accent)' }}
          />
          <Typography sx={{ color: 'var(--text-secondary)' }}>
            I have saved these codes somewhere I can reach without my phone.
          </Typography>
        </Box>
        <Button variant="contained" disabled={!saved} onClick={onAcknowledge}>
          Done
        </Button>
      </Box>
    </Box>
  );
};
